import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, OnChanges, SimpleChanges, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';

export interface TutorialStep {
  stepNumber: number;
  title: string;
  description: string;
  gifSrc: string;
  gifDuration: number; // Duration in milliseconds
  buttonText: string;
  isLastStep: boolean;
}

export interface TutorialConfig {
  sectionName: string; // 'about-us' or 'capabilities'
  steps: TutorialStep[];
  localStorageKey: string;
}

@Component({
  selector: 'app-tutorial-popup',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './tutorial-popup.component.html',
  styleUrls: ['./tutorial-popup.component.scss']
})
export class TutorialPopupComponent implements OnInit, OnDestroy, OnChanges {
  @Input() isVisible: boolean = false;
  @Input() config: TutorialConfig | null = null;
  @Output() tutorialCompleted = new EventEmitter<void>();
  @Output() tutorialClosed = new EventEmitter<void>();

  @ViewChild('gifElement') gifElement!: ElementRef<HTMLImageElement>;

  currentStepIndex: number = 0;
  isGifPlaying: boolean = false;
  isGifCompleted: boolean = false;
  gifTimeout: any = null;

  ngOnInit(): void {
    // Component initialization
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Check if isVisible changed from false to true
    if (changes['isVisible'] && this.isVisible && this.config) {
      // Use setTimeout to ensure the view is updated before starting the tutorial
      setTimeout(() => {
        this.startTutorial();
      }, 100);
    }
  }

  ngOnDestroy(): void {
    this.clearGifTimeout();
  }

  private startTutorial(): void {
    console.log('Starting tutorial with config:', this.config);
    this.currentStepIndex = 0;
    this.isGifPlaying = false;
    this.isGifCompleted = false;
    this.playCurrentStepGif();
  }

  private playCurrentStepGif(): void {
    if (!this.config || this.currentStepIndex >= this.config.steps.length) {
      return;
    }

    const currentStep = this.config.steps[this.currentStepIndex];
    console.log('Starting GIF playback for step:', currentStep.stepNumber, 'GIF src:', currentStep.gifSrc);
    
    this.isGifPlaying = true;
    this.isGifCompleted = false;

    // Set timeout to simulate GIF completion
    this.gifTimeout = setTimeout(() => {
      console.log('GIF playback completed for step:', currentStep.stepNumber);
      this.onGifCompleted();
    }, currentStep.gifDuration);
  }

  private onGifCompleted(): void {
    this.isGifPlaying = false;
    this.isGifCompleted = true;
  }

  onGifLoaded(): void {
    console.log('GIF loaded successfully');
  }

  onGifError(event: any): void {
    console.error('GIF failed to load:', event);
    // If GIF fails to load, still show the content after a delay
    setTimeout(() => {
      this.onGifCompleted();
    }, 2000);
  }

  onContinue(): void {
    if (!this.config) return;

    const currentStep = this.config.steps[this.currentStepIndex];
    
    if (currentStep.isLastStep) {
      this.completeTutorial();
    } else {
      this.nextStep();
    }
  }

  private nextStep(): void {
    this.currentStepIndex++;
    this.playCurrentStepGif();
  }

  private completeTutorial(): void {
    // Mark tutorial as completed in localStorage
    if (this.config) {
      localStorage.setItem(this.config.localStorageKey, 'true');
    }
    
    this.tutorialCompleted.emit();
    this.closePopup();
  }

  onClose(): void {
    this.closePopup();
  }

  private closePopup(): void {
    this.clearGifTimeout();
    this.tutorialClosed.emit();
  }

  private clearGifTimeout(): void {
    if (this.gifTimeout) {
      clearTimeout(this.gifTimeout);
      this.gifTimeout = null;
    }
  }

  get currentStep(): TutorialStep | null {
    if (!this.config || this.currentStepIndex >= this.config.steps.length) {
      return null;
    }
    return this.config.steps[this.currentStepIndex];
  }

  get progressPercentage(): number {
    if (!this.config) return 0;
    return ((this.currentStepIndex + 1) / this.config.steps.length) * 100;
  }
}
